/**
 * ExecutionResult Enhanced Component
 * 
 * Unique Properties:
 * - Performance metrics dashboard with comparisons
 * - Success/failure analysis with categorization
 * - Error pattern detection and grouping
 * - Resource consumption tracking (CPU, memory, time)
 * - Execution timeline with bottleneck identification
 * - Result data visualization with charts
 * - Comparison mode for multiple executions
 */

import {
    Alert,
    Badge,
    Card,
    Container,
    Grid,
    Group,
    Paper,
    Progress,
    RingProgress,
    SegmentedControl,
    Stack,
    Tabs,
    Text,
    ThemeIcon,
    Timeline,
    Title
} from '@mantine/core';
import '@mantine/core/styles.css';
import {
    IconAlertTriangle,
    IconChartBar,
    IconCheck,
    IconClock,
    IconCpu,
    IconDatabase,
    IconFlame,
    IconTrendingDown,
    IconTrendingUp,
    IconX
} from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import WorkflowSearchMenu from './WorkflowSearchMenu';

interface ExecutionResult {
    id: string;
    status: 'success' | 'failure' | 'partial';
    duration_ms: number;
    timestamp: string;
    result_data: any;
    error?: string;
    metrics: {
        cpu_usage: number;
        memory_mb: number;
        io_operations: number;
    };
}

interface ExecutionResultProps {
    data?: ExecutionResult[];
    mode?: 'analyze' | 'compare' | 'view';
    onAction?: (action: string, data?: any) => void;
}

export const ExecutionResultEnhanced: React.FC<ExecutionResultProps> = ({
    data = [],
    mode = 'analyze'
}) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');
    const resultsTableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (resultsTableRef.current) {
            new TabulatorFull(resultsTableRef.current, {
                data: data,
                layout: 'fitColumns',
                responsiveLayout: 'hide',
                dataTree: true,
                dataTreeStartExpanded: false,
                columns: [
                    { title: 'ID', field: 'id', headerFilter: true },
                    {
                        title: 'Status',
                        field: 'status',
                        formatter: (cell: any) => {
                            const value = cell.getValue();
                            const colors: Record<string, string> = { success: 'green', failure: 'red', partial: 'orange' };
                            const color = colors[value as string] || 'black';
                            return `<span style="color: ${color};">${value}</span>`;
                        }
                    },
                    { title: 'Duration', field: 'duration_ms', formatter: (cell: any) => `${cell.getValue()}ms` },
                    { title: 'Timestamp', field: 'timestamp' }
                ]
            });
        }
    }, [data]);

    const calculateMetrics = () => {
        const total = data.length;
        const successful = data.filter(r => r.status === 'success').length;
        const failed = data.filter(r => r.status === 'failure').length;
        const avgDuration = data.reduce((acc, r) => acc + r.duration_ms, 0) / total || 0;

        return {
            successRate: Math.round((successful / total) * 100) || 0,
            failureRate: Math.round((failed / total) * 100) || 0,
            avgDuration: Math.round(avgDuration),
            totalExecutions: total
        };
    };

    const metrics = calculateMetrics();

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Group justify="space-between" mb="lg">
                    <div>
                        <Title order={2}>Execution Results</Title>
                        <Text color="dimmed">Analyze workflow execution outcomes and performance</Text>
                    </div>
                    <Group>
                        <RingProgress
                            size={80}
                            thickness={8}
                            sections={[
                                { value: metrics.successRate, color: 'green' },
                                { value: metrics.failureRate, color: 'red' }
                            ]}
                            label={
                                <Text size="xs" ta="center">
                                    {metrics.successRate}%
                                    <br />
                                    <Text size="xs" color="dimmed">Success</Text>
                                </Text>
                            }
                        />
                        <SegmentedControl
                            value={viewMode}
                            onChange={(value) => setViewMode(value as any)}
                            data={[
                                { label: 'Table', value: 'table' },
                                { label: 'Chart', value: 'chart' }
                            ]}
                        />
                    </Group>
                </Group>

                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
                            Overview
                        </Tabs.Tab>
                        <Tabs.Tab value="metrics" leftSection={<IconTrendingUp size={16} />}>
                            Metrics
                        </Tabs.Tab>
                        <Tabs.Tab value="errors" leftSection={<IconAlertTriangle size={16} />}>
                            Errors
                        </Tabs.Tab>
                        <Tabs.Tab value="performance" leftSection={<IconFlame size={16} />}>
                            Performance
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="overview">
                        <Grid mt="md">
                            <Grid.Col span={3}>
                                <Card withBorder>
                                    <Group justify="space-between" mb="xs">
                                        <Text size="sm" color="dimmed">Success Rate</Text>
                                        <ThemeIcon color="green" variant="light">
                                            <IconCheck size={16} />
                                        </ThemeIcon>
                                    </Group>
                                    <Title order={3}>{metrics.successRate}%</Title>
                                    <Progress value={metrics.successRate} color="green" mt="xs" />
                                </Card>
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <Card withBorder>
                                    <Group justify="space-between" mb="xs">
                                        <Text size="sm" color="dimmed">Avg Duration</Text>
                                        <ThemeIcon color="blue" variant="light">
                                            <IconClock size={16} />
                                        </ThemeIcon>
                                    </Group>
                                    <Title order={3}>{metrics.avgDuration}ms</Title>
                                </Card>
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <Card withBorder>
                                    <Group justify="space-between" mb="xs">
                                        <Text size="sm" color="dimmed">Total Executions</Text>
                                        <ThemeIcon color="orange" variant="light">
                                            <IconDatabase size={16} />
                                        </ThemeIcon>
                                    </Group>
                                    <Title order={3}>{metrics.totalExecutions}</Title>
                                </Card>
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <Card withBorder>
                                    <Group justify="space-between" mb="xs">
                                        <Text size="sm" color="dimmed">Failure Rate</Text>
                                        <ThemeIcon color="red" variant="light">
                                            <IconX size={16} />
                                        </ThemeIcon>
                                    </Group>
                                    <Title order={3}>{metrics.failureRate}%</Title>
                                    <Progress value={metrics.failureRate} color="red" mt="xs" />
                                </Card>
                            </Grid.Col>
                        </Grid>

                        <Card withBorder mt="md">
                            <Title order={5} mb="md">Execution Results Table</Title>
                            <div ref={resultsTableRef} />
                        </Card>
                    </Tabs.Panel>

                    <Tabs.Panel value="metrics">
                        <Stack mt="md">
                            <Alert icon={<IconTrendingUp size={16} />} color="blue">
                                Performance metrics and resource utilization analysis
                            </Alert>
                            <Grid>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">Resource Usage</Title>
                                        <Stack gap="xs">
                                            <Group justify="space-between">
                                                <Group gap="xs">
                                                    <IconCpu size={16} />
                                                    <Text size="sm">CPU Usage</Text>
                                                </Group>
                                                <Badge>45%</Badge>
                                            </Group>
                                            <Progress value={45} />

                                            <Group justify="space-between">
                                                <Group gap="xs">
                                                    <IconDatabase size={16} />
                                                    <Text size="sm">Memory</Text>
                                                </Group>
                                                <Badge>256 MB</Badge>
                                            </Group>
                                            <Progress value={60} color="orange" />
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">Execution Trends</Title>
                                        <Timeline active={-1}>
                                            <Timeline.Item title="Peak Performance">
                                                <Text size="sm" color="dimmed">10:00 AM - 50ms avg</Text>
                                            </Timeline.Item>
                                            <Timeline.Item title="Degradation Detected" color="orange">
                                                <Text size="sm" color="dimmed">2:00 PM - 150ms avg</Text>
                                            </Timeline.Item>
                                            <Timeline.Item title="Recovery">
                                                <Text size="sm" color="dimmed">4:00 PM - 75ms avg</Text>
                                            </Timeline.Item>
                                        </Timeline>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="errors">
                        <Stack mt="md">
                            <Alert icon={<IconAlertTriangle size={16} />} color="red">
                                Error analysis and pattern detection
                            </Alert>
                            <Card withBorder>
                                <Title order={5} mb="md">Error Categories</Title>
                                <Stack gap="xs">
                                    <Group justify="space-between">
                                        <Text>Network Timeout</Text>
                                        <Badge color="red">12 occurrences</Badge>
                                    </Group>
                                    <Group justify="space-between">
                                        <Text>Invalid Input</Text>
                                        <Badge color="orange">8 occurrences</Badge>
                                    </Group>
                                    <Group justify="space-between">
                                        <Text>Permission Denied</Text>
                                        <Badge color="yellow">3 occurrences</Badge>
                                    </Group>
                                </Stack>
                            </Card>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="performance">
                        <Stack mt="md">
                            <Alert icon={<IconFlame size={16} />} color="orange">
                                Performance bottlenecks and optimization opportunities
                            </Alert>
                            <Card withBorder>
                                <Title order={5} mb="md">Bottleneck Analysis</Title>
                                <Stack gap="xs">
                                    <div>
                                        <Group justify="space-between" mb="xs">
                                            <Text size="sm">Database Queries</Text>
                                            <Text size="sm" color="dimmed">45% of total time</Text>
                                        </Group>
                                        <Progress value={45} color="red" />
                                    </div>
                                    <div>
                                        <Group justify="space-between" mb="xs">
                                            <Text size="sm">API Calls</Text>
                                            <Text size="sm" color="dimmed">30% of total time</Text>
                                        </Group>
                                        <Progress value={30} color="orange" />
                                    </div>
                                    <div>
                                        <Group justify="space-between" mb="xs">
                                            <Text size="sm">Data Processing</Text>
                                            <Text size="sm" color="dimmed">15% of total time</Text>
                                        </Group>
                                        <Progress value={15} color="yellow" />
                                    </div>
                                    <div>
                                        <Group justify="space-between" mb="xs">
                                            <Text size="sm">Other</Text>
                                            <Text size="sm" color="dimmed">10% of total time</Text>
                                        </Group>
                                        <Progress value={10} color="green" />
                                    </div>
                                </Stack>
                            </Card>

                            <Card withBorder>
                                <Title order={5} mb="md">Optimization Suggestions</Title>
                                <Stack gap="xs">
                                    <Group>
                                        <ThemeIcon color="blue" variant="light">
                                            <IconTrendingDown size={16} />
                                        </ThemeIcon>
                                        <div>
                                            <Text size="sm" fw={500}>Implement query caching</Text>
                                            <Text size="xs" color="dimmed">Could reduce database time by 60%</Text>
                                        </div>
                                    </Group>
                                    <Group>
                                        <ThemeIcon color="green" variant="light">
                                            <IconTrendingUp size={16} />
                                        </ThemeIcon>
                                        <div>
                                            <Text size="sm" fw={500}>Batch API requests</Text>
                                            <Text size="xs" color="dimmed">Reduce API calls by 40%</Text>
                                        </div>
                                    </Group>
                                </Stack>
                            </Card>
                        </Stack>
                    </Tabs.Panel>
                </Tabs>
            </Paper>
        </Container>
    );
};

export default ExecutionResultEnhanced;
